<script setup lang="ts">
import RBButton from '../../components/Button.vue'
import {computed, ref} from 'vue'
import {useNewUiEnabled} from '../../composable/useNewUiEnabled'
import {useDpaStore} from '../../stores/dpa'

const dpaStore = useDpaStore()
const isNewUiEnabled = useNewUiEnabled()
const languageLabel = computed(() => (dpaStore.language === 'de' ? 'Sprache' : 'Language'))
const printLabel = computed(() => (dpaStore.language === 'de' ? 'Drucken' : 'Print'))

const showDialog = ref(false)

function changeLanguage(lang: string) {
  dpaStore.setLanguage(lang)
  if (isNewUiEnabled.value) showDialog.value = false
}

function print() {
  window.print()
}

function onMouseEnter() {
  showDialog.value = true
}

function onMouseLeave() {
  showDialog.value = false
}

function toggleHandler() {
  if (showDialog.value) return
  showDialog.value = !showDialog.value
}
</script>

<template>
  <header
    v-if="isNewUiEnabled"
    class="sticky top-0 left-0 z-header flex h-20 w-full items-center justify-center bg-branddark print:hidden"
  >
    <div class="flex w-full max-w-screen-new-lg flex-row items-center justify-between gap-2 px-new-lg new-sm:px-new-4xl md:gap-4">
      <a class="shrink-0" href="#">
        <img alt="RateBoard" class="h-9 w-auto" src="/logo_home.png" title="RateBoard" />
      </a>
      <div class="flex shrink-0 flex-row items-center gap-2 new-sm:gap-8">
        <div class="relative w-auto max-w-56 md:w-full" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
          <button
            aria-controls="dpa-language-menu"
            :aria-expanded="showDialog"
            aria-haspopup="menu"
            class="flex h-12 w-auto items-center gap-1 bg-transparent text-new-base uppercase tracking-widest text-white outline-none transition-colors hover:text-brandlight focus-visible:text-brandlight md:w-full"
            type="button"
            @click="toggleHandler"
            @keydown.esc="showDialog = false"
          >
            <i aria-hidden="true" class="fa-solid fa-globe shrink-0 text-[16px] leading-none"></i>
            <span class="flex items-center gap-1">
              <span>{{ languageLabel }}</span>
              <i aria-hidden="true" class="fa-solid fa-chevron-down shrink-0 text-[8px] leading-none"></i>
            </span>
          </button>
          <transition
            enter-active-class="duration-75 ease-in-out"
            enter-from-class="-translate-y-1/4 scale-y-0 opacity-0"
            enter-to-class="translate-y-0 scale-y-100 opacity-100"
            leave-active-class="duration-75 ease-in-out"
            leave-from-class="translate-y-0 scale-y-100 opacity-100"
            leave-to-class="-translate-y-1/4 scale-y-0 opacity-0"
          >
            <div
              v-if="showDialog"
              id="dpa-language-menu"
              class="absolute top-full left-1/2 z-header flex w-[150px] -translate-x-1/2 flex-col gap-[10px] rounded-b-xl bg-branddark px-4 py-3 shadow-md"
              role="menu"
            >
              <button
                class="text-left text-new-base leading-none text-new-tertiary-default transition-colors hover:text-new-tertiary-300 focus-visible:text-new-tertiary-300"
                role="menuitem"
                type="button"
                @click="changeLanguage('en')"
              >
                English
              </button>
              <button
                class="text-left text-new-base leading-none text-new-tertiary-default transition-colors hover:text-new-tertiary-300 focus-visible:text-new-tertiary-300"
                role="menuitem"
                type="button"
                @click="changeLanguage('de')"
              >
                Deutsch
              </button>
            </div>
          </transition>
        </div>
        <button
          class="flex h-12 w-auto max-w-40 items-center justify-start gap-1 bg-transparent text-new-base uppercase tracking-widest text-white outline-none transition-colors hover:text-brandlight focus-visible:text-brandlight md:w-full"
          type="button"
          @click="print"
        >
          <i aria-hidden="true" class="fa-solid fa-print shrink-0 text-[16px] leading-none"></i>
          <span>{{ printLabel }}</span>
        </button>
      </div>
    </div>
  </header>
  <div v-else class="fixed flex h-20 w-full flex-row items-center bg-branddark print:hidden z-header">
    <div class="w-5/6">
      <a href="#"><img alt="RateBoard" class="ml-6" src="/logo_mini_white.svg" title="RateBoard" /></a>
    </div>
    <div @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
      <RBButton class="sm:w-44 text-base" @click="toggleHandler">
        <i class="fa-solid fa-caret-down pr-2"></i>
        <i class="fa fa-globe fa-lg"></i> {{ languageLabel }}
      </RBButton>
      <transition
        enter-active-class="duration-75 ease-in-out"
        enter-from-class="-translate-y-1/4 scale-y-0 opacity-0"
        enter-to-class="translate-y-0 scale-y-100 opacity-100"
        leave-active-class="duration-75 ease-in-out"
        leave-from-class="translate-y-0 scale-y-100 opacity-100"
        leave-to-class="-translate-y-1/4 scale-y-0 opacity-0"
      >
        <div v-if="showDialog">
          <div class="absolute flex w-44 flex-col overflow-x-hidden bg-branddark text-lg leading-6 text-white shadow-md">
            <button class="bg-branddark p-2 hover:bg-branddarkdarker" type="button" @click="changeLanguage('en')">English</button>
            <button class="bg-branddark p-2 hover:bg-branddarkdarker" type="button" @click="changeLanguage('de')">Deutsch</button>
          </div>
        </div>
      </transition>
    </div>
    <div>
      <RBButton class="sm:w-44 text-base" variant="primary" @click="print">
        <i class="fa fa-print fa-lg"></i> {{ printLabel }}
      </RBButton>
    </div>
  </div>
</template>
